import dayjs from 'dayjs';
import { defineFakeRoute } from 'vite-plugin-fake-server/client';
import type { PlatformHomeSummary } from '@/pages/business/dashboard/operating-home/service';
import { bucketTrend } from '@/pages/business/dashboard/operating-home/trendBuckets';
import { protectBusinessRoutes } from './business/permissions';
import { resultError, resultSuccess } from './business/utils';
import { gatewayInstances } from './gateway/fixtures';

// Page fixtures only: deterministic samples, no gateway state or upstream calls.
const channels = [
  ['codex-main', 'Codex 主渠道', 26],
  ['gemini-main', 'Gemini 主渠道', 17],
  ['grok-main', 'Grok 主渠道', 15],
  ['claude-main', 'Claude 主渠道', 12],
  ['codex-backup', 'Codex 备用渠道', 9],
  ['gemini-backup', 'Gemini 备用渠道', 8],
  ['grok-backup', 'Grok 备用渠道', 7],
  ['claude-backup', 'Claude 备用渠道', 6],
] as const;
const providers = [
  ['codex', 'Codex'],
  ['grok', 'Grok'],
  ['gemini', 'Gemini'],
  ['claude', 'Claude'],
] as const;
// Deterministic daily series: a weekday rhythm, slow drift and a varying failure
// rate, so no two days repeat while the same date always yields the same numbers.
const weekdayFactor = [0.72, 1, 1.06, 1.08, 1.04, 0.98, 0.76];
function daySample(date: dayjs.Dayjs) {
  const index = date.diff(dayjs('2026-01-01'), 'day');
  const drift = 1 + 0.12 * Math.sin(index / 9) + 0.05 * Math.sin(index / 3.7 + 1);
  const total = Math.round(15200 * weekdayFactor[date.day()] * drift);
  const failureRate =
    0.007 +
    0.005 * ((1 + Math.sin(index / 5.3)) / 2) +
    0.002 * ((1 + Math.sin(index * 1.7)) / 2);
  const failedCount = Math.round(total * failureRate);
  const successCount = total - failedCount;
  return {
    successCount,
    failedCount,
    tokenCount: Math.round(successCount * (1380 + 220 * Math.sin(index / 6.1))),
  };
}
// One scenario shared by every figure on the page: health and circuit-breaker
// state are independent, so an abnormal channel can still have a closed breaker.
const instances = gatewayInstances;
const channelStatus: PlatformHomeSummary['channelStatus'] = [
  { id: 'codex-main', name: 'Codex 主渠道', health: 'healthy', breaker: 'closed' },
  { id: 'gemini-main', name: 'Gemini 主渠道', health: 'healthy', breaker: 'closed' },
  { id: 'grok-main', name: 'Grok 主渠道', health: 'healthy', breaker: 'closed' },
  {
    id: 'claude-main',
    name: 'Claude 主渠道',
    health: 'abnormal',
    breaker: 'half_open',
    reason: '连续 3 次探测超时，正在试探恢复',
  },
  { id: 'codex-backup', name: 'Codex 备用渠道', health: 'healthy', breaker: 'closed' },
  {
    id: 'gemini-backup',
    name: 'Gemini 备用渠道',
    health: 'unknown',
    breaker: 'closed',
  },
  {
    id: 'grok-backup',
    name: 'Grok 备用渠道',
    health: 'abnormal',
    breaker: 'open',
    reason: '上游返回鉴权失败，已熔断',
  },
  {
    id: 'claude-backup',
    name: 'Claude 备用渠道',
    health: 'healthy',
    breaker: 'closed',
  },
];

export default protectBusinessRoutes(
  defineFakeRoute({
    url: '/platform/dashboard/operations',
    method: 'get',
    response: ({ query }) => {
      try {
        const now = Date.now();
        const timeZone =
          typeof query.time_zone === 'string' ? query.time_zone : 'Asia/Shanghai';
        const today = new Intl.DateTimeFormat('en-CA', {
          timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(new Date(now));
        const from = dayjs(
          typeof query.from === 'string'
            ? query.from
            : dayjs(today).subtract(6, 'day').format('YYYY-MM-DD'),
        );
        const to = dayjs(typeof query.to === 'string' ? query.to : today);
        const length = to.diff(from, 'day') + 1;
        if (
          !from.isValid() ||
          !to.isValid() ||
          length < 1 ||
          length > 366 ||
          to.isAfter(dayjs(today), 'day')
        ) {
          return resultError('Invalid dashboard date range', 422);
        }
        const days = Array.from({ length }, (_, index) => {
          const date = from.add(index, 'day');
          return { date: date.format('YYYY-MM-DD'), ...daySample(date) };
        });
        const successCount = days.reduce((sum, day) => sum + day.successCount, 0);
        const failedCount = days.reduce((sum, day) => sum + day.failedCount, 0);
        const tokenCount = days.reduce((sum, day) => sum + day.tokenCount, 0);
        const requestCount = successCount + failedCount;
        const todaySample = daySample(dayjs(today));
        const trend = bucketTrend(
          days,
          ['successCount', 'failedCount', 'tokenCount'],
          timeZone,
          now,
        );
        // Channel shares are fixed weights of the period total; the largest
        // channel takes the rounding remainder so the parts always add up.
        const channelCounts = channels.map(([, , weight]) =>
          Math.floor((requestCount * weight) / 100),
        );
        channelCounts[0] += requestCount - channelCounts.reduce((sum, v) => sum + v, 0);
        const providerCount = (key: string) =>
          channels.reduce(
            (sum, [id], index) =>
              id.startsWith(key) ? sum + channelCounts[index] : sum,
            0,
          );
        const result: PlatformHomeSummary = {
          granularity: trend.granularity,
          timeZone,
          requestCount: todaySample.successCount + todaySample.failedCount,
          successCount: todaySample.successCount,
          failedCount: todaySample.failedCount,
          instances: {
            total: instances.length,
            ready: instances.filter(({ status }) => status === 'ready').length,
            draining: instances.filter(({ status }) => status === 'draining').length,
            offline: instances.filter(({ status }) => status === 'offline').length,
          },
          channelStatus,
          tokenTrend: trend.points.map((day) => ({
            date: day.date,
            tokenCount: day.tokenCount,
          })),
          overview: {
            requestCount,
            successCount,
            failedCount,
            tokenCount,
            days: trend.points,
          },
          ranking: channels.map(([id, name], index) => ({
            id: `channel-${id}`,
            name,
            value: channelCounts[index],
          })),
          distribution: providers.map(([id, name]) => ({
            id,
            name,
            value: providerCount(id),
          })),
        };
        return resultSuccess(result);
      } catch {
        return resultError('Invalid dashboard parameters', 422);
      }
    },
  }),
);
